import React, {
  createContext,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export const MediaContext = createContext(null);

function parseQueryAgainstWindow(query) {
  try {
    const maxMatch = /max-width:\s*(\d+)px/.exec(query);
    const minMatch = /min-width:\s*(\d+)px/.exec(query);
    const width = typeof window !== 'undefined' ? window.innerWidth : 0;

    let result = true;
    if (maxMatch) {
      result = result && width <= Number(maxMatch[1]);
    }
    if (minMatch) {
      result = result && width >= Number(minMatch[1]);
    }
    return result;
  } catch {
    return false;
  }
}

export function MediaProvider({ children }) {
  const queriesRef = useRef(new Map()); // query -> { mql, onChange }
  const [matches, setMatches] = useState({}); // { [query]: boolean }

  const supportsMatchMedia =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function';

  // Window resize fallback for environments without matchMedia
  const resizeFallbackRef = useRef(false);
  useEffect(() => {
    if (!supportsMatchMedia && typeof window !== 'undefined') {
      const onResize = () => {
        startTransition(() => {
          setMatches((prev) => {
            const updated = { ...prev };
            for (const query of Object.keys(updated)) {
              updated[query] = parseQueryAgainstWindow(query);
            }
            return updated;
          });
        });
      };
      window.addEventListener('resize', onResize);
      resizeFallbackRef.current = true;
      return () => window.removeEventListener('resize', onResize);
    }
    return undefined;
  }, [supportsMatchMedia]);

  const registerQuery = useCallback(
    (query) => {
      if (typeof window === 'undefined') return; // noop on SSR
      if (queriesRef.current.has(query)) return; // already tracking

      if (supportsMatchMedia) {
        const mql = window.matchMedia(query);
        const onChange = (event) => {
          startTransition(() => {
            setMatches((prev) => ({ ...prev, [query]: event.matches }));
          });
        };
        // Defer the first client-only media result so Suspense boundaries can
        // finish hydrating against their SSR defaults before content changes.
        startTransition(() => {
          setMatches((prev) => ({ ...prev, [query]: mql.matches }));
        });

        if (typeof mql.addEventListener === 'function') {
          mql.addEventListener('change', onChange);
        } else if (typeof mql.addListener === 'function') {
          mql.addListener(onChange);
        }
        queriesRef.current.set(query, { mql, onChange });
      } else {
        // Fallback: evaluate immediately and rely on window resize listener to update
        startTransition(() => {
          setMatches((prev) => ({ ...prev, [query]: parseQueryAgainstWindow(query) }));
        });
        queriesRef.current.set(query, { mql: null, onChange: null });
      }
    },
    [supportsMatchMedia],
  );

  const unregisterAll = useCallback(() => {
    for (const [, { mql, onChange }] of queriesRef.current.entries()) {
      if (mql && onChange) {
        if (typeof mql.removeEventListener === 'function') {
          mql.removeEventListener('change', onChange);
        } else if (typeof mql.removeListener === 'function') {
          mql.removeListener(onChange);
        }
      }
    }
    queriesRef.current.clear();
  }, []);

  useEffect(() => {
    return () => unregisterAll();
  }, [unregisterAll]);

  const getMatch = useCallback(
    (query, defaultValue = false) => {
      if (Object.prototype.hasOwnProperty.call(matches, query)) return matches[query];
      // Keep the first client render aligned with SSR output.
      // Queries are registered after mount and then updated from the provider.
      return defaultValue;
    },
    [matches],
  );

  const contextValue = useMemo(
    () => ({ matches, registerQuery, getMatch }),
    [matches, registerQuery, getMatch],
  );

  return <MediaContext.Provider value={contextValue}>{children}</MediaContext.Provider>;
}

export default MediaProvider;
